/**
 * TagMark - Tiptap extension for template tag marks
 *
 * Creates inline marks for styled text segments (e.g., <highlight>text</highlight>)
 * The mark stores only the tagName; actual styling is applied from parameters.labels dictionary.
 *
 * Format: <tagName>styled text content</tagName>
 */

import { Mark, mergeAttributes } from '@tiptap/core';

export interface TagMarkAttrs {
  /** Tag name (e.g., "highlight", "bold", "emphasis") */
  tagName: string;
}

export const TagMark = Mark.create({
  name: 'templateTag',

  addAttributes() {
    return {
      tagName: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-tag-name'),
        renderHTML: (attributes) => ({
          'data-tag-name': attributes.tagName,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-tag-name]',
        getAttrs: (domNode) => {
          const dom = domNode as HTMLElement;
          return {
            tagName: dom.getAttribute('data-tag-name'),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'template-tag',
        'data-type': 'templateTag',
        style: 'background-color: rgba(227, 242, 253, 0.2); border-radius: 4px; padding: 0 4px;',
      }),
      0, // 0 means "put content here"
    ];
  },

  /**
   * Add commands to apply/remove tag marks
   */
  addCommands() {
    return {
      setTagMark:
        (tagName: string) =>
        ({ commands }) => {
          return commands.setMark(this.name, { tagName });
        },
      unsetTagMark:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
      toggleTagMark:
        (tagName: string) =>
        ({ commands }) => {
          return commands.toggleMark(this.name, { tagName });
        },
    };
  },
});

/**
 * Declare custom commands for TypeScript
 */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    templateTag: {
      setTagMark: (tagName: string) => ReturnType;
      unsetTagMark: () => ReturnType;
      toggleTagMark: (tagName: string) => ReturnType;
    };
  }
}

export default TagMark;
